import { getPreset, getSourceId, Scenario } from '@account/account-portal/config'
import {
  closeAccountPortal,
  registerLightUser,
  Touchpoint,
  useLightRegistrationStatus,
} from '@account/account-portal/lib'
import { LightRegistrationData } from '@account/account-portal/types'
import { Consents } from '@account/consents'
import { showErrors as showConsentErrors } from '@account/consents-shared-lib'
import { GlButton } from '@adl/component-button'
import { GlForm } from '@adl/component-form'
import { GlInput } from '@adl/component-input'
import { useConfig } from '@crystal/config'
import { useDevice, useMfProps } from '@crystal/core'
import { Translations, useTranslations } from '@crystal/translator'
import { FC, useState } from 'react'
import '@account/consents/style.css'

import { AccountPortalConfig } from '../../config'
import { useConsents, useViewData } from '../../lib'
import { isMarketingConsentAccepted, isPaidMediaConsentAccepted, trackLightAccountCreation } from '../../lib/analytics'
import { AgeConfirmationCheckBox } from '../AgeConfirmationCheckBox'
import { ErrorView } from '../ErrorView'

import { useFormData } from './account-form'

export const SubscriptionLandingForm: FC = () => {
  const t = useTranslations<Translations>()
  const { emailPolicy, legalPolicy } = useConfig<AccountPortalConfig>()
  const { isPending } = useLightRegistrationStatus()
  const { registerField, handleSubmit, setError } = useFormData<LightRegistrationData>()
  const { isMobile } = useDevice()
  const scenarioType = 'registration'
  const consentsState = useConsents(scenarioType)
  const mfProps = useMfProps()
  const { viewData, updateViewData } = useViewData()
  const { isAuthProvider } = getPreset(viewData.preset)
  const touchpoint = viewData.touchpoint || Touchpoint.Unknown
  const registrationSourceId = getSourceId(touchpoint, Scenario.Registration)
  const prefillData = viewData.pageContext?.prefillData
  const [apiError, setApiError] = useState<boolean>()

  async function onSubmit({ email, persistentLogin = !!isAuthProvider, minAgeConfirmation }: LightRegistrationData) {
    const checkPaidMediaConsentAccepted = isPaidMediaConsentAccepted(consentsState?.payload)
    const checkMarketingConsentAccepted = isMarketingConsentAccepted(consentsState?.payload)

    if (!consentsState?.isValid) {
      showConsentErrors(scenarioType)
      return
    }

    if (legalPolicy.minAgeRequired && !minAgeConfirmation) {
      setError('minAgeConfirmation', {
        message: t('forms.date.tooyoung'),
      })
      return
    }

    const lightAccountRequest: LightRegistrationData = {
      email,
      minAgeConfirmation: legalPolicy.minAgeRequired ? minAgeConfirmation : true,
      sourceId: registrationSourceId,
      persistentLogin,
      legalAcceptance: consentsState?.payload?.legalAcceptance,
      createMember: true,
      sendActivationEmail: false,
      isSubscription: true,
    }

    try {
      const { acid, access_token, refresh_token } = await registerLightUser(lightAccountRequest)
      const isNewLightUser = !!access_token
      updateViewData({
        accessToken: access_token,
        refreshToken: refresh_token,
      })
      if (isNewLightUser) {
        trackLightAccountCreation(acid, email, checkPaidMediaConsentAccepted, checkMarketingConsentAccepted)
      }
      closeAccountPortal()
    } catch {
      setApiError(true)
    }
  }

  return (
    <GlForm noValidate onSubmit={handleSubmit(onSubmit)}>
      <GlInput
        id="email"
        type="email"
        autoComplete="email"
        className="gl-form-item gl-vspace-bpall-small"
        labelText={t('email.label')}
        aria-label={t('email.label')}
        readOnly={prefillData?.email && !prefillData?.email?.editable}
        {...registerField('email', {
          ...emailPolicy,
          errorMessage: t('email.error'),
        })}
      />
      {legalPolicy.minAgeRequired && (
        <AgeConfirmationCheckBox
          scenarioType={scenarioType}
          {...registerField('minAgeConfirmation', {
            type: 'checkbox',
            propsMapper: 'GlCheckbox.v5',
          })}
          required
        />
      )}
      <Consents {...mfProps} explicit scenarioType={scenarioType} />
      <ErrorView error={apiError} />
      <GlButton
        fullWidth={isMobile}
        type="submit"
        className="gl-vspace-bpall-small"
        aria-label={t('landing.button.label')}
        loading={isPending}
        disabled={isPending}
        id="light-account-only-form-button">
        {t('landing.button.label')}
      </GlButton>
      <Consents {...mfProps} implicit scenarioType={scenarioType} />
    </GlForm>
  )
}
